// CHG-033: the real TrackerIo, built from browser globals. The globals are a
// parameter so the binding itself is testable without a DOM (wiring.test.ts).
import type { TrackerIo } from './tracker.ts';

export interface BrowserIoEnv {
  fetch: typeof fetch;
  navigator: { sendBeacon?: (url: string, data?: BodyInit | null) => boolean };
  crypto: Pick<Crypto, 'getRandomValues'> & { randomUUID?: () => string };
  addEventListener: (type: 'pagehide', fn: () => void) => void;
  removeEventListener: (type: 'pagehide', fn: () => void) => void;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

/** RFC 4122 v4 layout from 16 random bytes (randomUUID is secure-context only). */
function uuidFromRandomValues(crypto: BrowserIoEnv['crypto']): string {
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createBrowserIo(env: BrowserIoEnv = globalThis as unknown as BrowserIoEnv): TrackerIo {
  const { navigator, crypto } = env;
  const io: TrackerIo = {
    // No Content-Type header: a simple request needs no CORS preflight, and the
    // backend parses the raw body whatever its type. The default credentials
    // mode is same-origin, so omit is explicit.
    post: (url, body) =>
      env.fetch(url, { method: 'POST', body, keepalive: true, credentials: 'omit' }).then((): void => undefined),
    now: () => new Date(),
    randomId: () => (typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : uuidFromRandomValues(crypto)),
    setTimeout: (fn, ms) => env.setTimeout(fn, ms),
    clearTimeout: (handle) => env.clearTimeout(handle),
    onUnload: (fn) => {
      env.addEventListener('pagehide', fn);
      return () => env.removeEventListener('pagehide', fn);
    },
  };
  if (typeof navigator?.sendBeacon === 'function') {
    io.beacon = (url, body) => navigator.sendBeacon!(url, body);
  }
  return io;
}
